import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { DividerModule } from 'primeng/divider';
import { AnimalModel } from '../../models/animal.model';
import { AnimalMediaCategoryModel } from '../../models/animal-media-category.model';
import { AnimalDocumentCategory } from '../../models/animal-document-category.enum';
import { AnimalDocumentsRepository } from '../../states/animal-documents.repository';
import { AnimalDocumentService } from '../../services/animal-document.service';
import { WaitingDialogService } from '../../../../shared/services/waiting-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ALLOWED_DOCUMENT_ACCEPT, DocumentFileMeta, getDocumentFileMeta } from '../document-file-type.util';

interface SelectedDocument {
  file: File;
  category: AnimalDocumentCategory;
  uiDate: Date;
}

@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TranslocoModule,
    FileUploadModule, ButtonModule, DialogModule, SelectModule, DatePickerModule, DividerModule
  ]
})
export class DocumentUploadComponent {
  @ViewChild('fileUpload') fileUpload!: FileUpload;
  @Input() animal: AnimalModel | null = null;
  @Output() documentsUploaded = new EventEmitter<number[]>();

  selectedFiles: SelectedDocument[] = [];
  dialogVisible = false;
  categoryOptions: AnimalMediaCategoryModel[] = [];
  readonly acceptTypes = ALLOWED_DOCUMENT_ACCEPT;

  constructor(
    private readonly _waitingDialogService: WaitingDialogService,
    private readonly _documentsRepository: AnimalDocumentsRepository,
    private readonly _documentService: AnimalDocumentService,
    private readonly _toastService: ToastService
  ) {
  }

  onFileSelect(): void {
    const files: File[] = this.fileUpload?.files ?? [];
    if (!files.length) return;

    this.selectedFiles = files.map((file: File) => ({
      file,
      category: AnimalDocumentCategory.OTHER,
      uiDate: new Date()
    }));

    this.dialogVisible = true;
  }

  openDialog(): void {
    this.categoryOptions = this._documentService.getAnimalDocumentCategories();
    this.dialogVisible = true;
  }

  onClearSelection(): void {
    this.selectedFiles = [];
    this.fileUpload?.clear();
  }

  onDialogHide(): void {
    this.onClearSelection();
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);

    const remainingFiles = this.selectedFiles.map(f => f.file);
    this.fileUpload.clear();
    if (remainingFiles.length) {
      Object.defineProperty(this.fileUpload, 'files', {
        value: remainingFiles,
        writable: true
      });
      this.onFileSelect();
    }
  }

  uploadFiles(): void {
    if (!this.animal || !this.selectedFiles.length) return;

    this._waitingDialogService.showWaitingDialog(true);
    let uploadCount = 0;
    const uploadedIds: number[] = [];

    for (const entry of this.selectedFiles) {
      const formData = new FormData();
      formData.append('file', entry.file);
      formData.append('animal_id', this.animal.id.toString());
      formData.append('category', entry.category);
      formData.append('date', this.toIsoDate(entry.uiDate));

      this._documentsRepository.addDocument(formData, this.animal.id).subscribe({
        next: (response) => {
          uploadCount++;
          uploadedIds.push(response.id);
          if (uploadCount === this.selectedFiles.length) {
            this.documentsUploaded.emit(uploadedIds);
            this._toastService.showSuccess('Dateien erfolgreich hochgeladen.');
            this.fileUpload.clear();
            this._waitingDialogService.showWaitingDialog(false);
            this.dialogVisible = false;
            this.onClearSelection();
          }
        },
        error: () => {
          this._toastService.showError('Fehler beim Hochladen.');
          this._waitingDialogService.showWaitingDialog(false);
        }
      });
    }
  }

  fileMeta(file: SelectedDocument): DocumentFileMeta {
    return getDocumentFileMeta(file.file.name);
  }

  private toIsoDate(date: Date): string {
    const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return utc.toISOString();
  }
}
